import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Slider,
  Rating,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PageLayout from './PageLayout';

const QUALITY_LABELS = {
  1: 'Muito ruim',
  2: 'Ruim',
  3: 'Regular',
  4: 'Boa',
  5: 'Excelente',
};

const WAKE_LABELS = {
  1: 'Muito cansado',
  2: 'Cansado',
  3: 'Neutro',
  4: 'Disposto',
  5: 'Muito disposto',
};

function SleepTips() {
  const tips = [
    'Mantenha horários consistentes para dormir e acordar, inclusive nos fins de semana.',
    'Evite telas (celular, computador, TV) pelo menos 30–60 minutos antes de dormir.',
    'Crie um ritual relaxante antes de dormir: leitura leve, meditação, respiração profunda.',
    'Evite cafeína e estimulantes no final da tarde e à noite.',
    'Mantenha o quarto escuro, silencioso e em temperatura agradável.',
    'Evite refeições muito pesadas perto do horário de dormir.',
  ];

  return (
    <Card sx={{ mt: 3, mb: 6 }}>
      <CardContent>
        <Typography variant="h6" color="primary.main" gutterBottom>
          Dicas de Higiene do Sono
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Pequenos ajustes na rotina podem melhorar muito a qualidade do seu sono.
        </Typography>
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {tips.map((tip, index) => (
            <li key={index}>
              <Typography variant="body2" color="text.primary">
                {tip}
              </Typography>
            </li>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Sleep({ customColors }) {
  const { currentUser } = useAuth();
  const [entries, setEntries] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState(7);
  const [quality, setQuality] = useState(4); // 1–5 estrelas
  const [wakeState, setWakeState] = useState(3); // 1–5 escala de disposição
  const [notes, setNotes] = useState('');
  const [editingDate, setEditingDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const loadSleep = async () => {
      if (!currentUser?.uid) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          const sleepEntries = (data.sleepEntries || []).map((e) => {
            let q = e.quality;
            if (typeof q !== 'number') {
              // fallback simples: converte strings antigas para número aproximado
              const map = {
                'Muito ruim': 1,
                Ruim: 2,
                Regular: 3,
                Boa: 4,
                Excelente: 5,
              };
              q = map[q] || 3;
            }
            const w = typeof e.wakeState === 'number' ? e.wakeState : 3;
            return { ...e, quality: q, wakeState: w };
          });

          // ordenar por data desc
          sleepEntries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          setEntries(sleepEntries);
        }
      } catch (error) {
        console.error('Erro ao carregar diário de sono:', error);
      }
    };
    loadSleep();
  }, [currentUser]);

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    if (!date) {
      setSnackbar({
        open: true,
        message: 'Preencha a data.',
        severity: 'warning',
      });
      return;
    }

    const hoursNumber = Number(hours);
    if (Number.isNaN(hoursNumber) || hoursNumber <= 0) {
      setSnackbar({
        open: true,
        message: 'Informe um número válido de horas de sono.',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentEntries = data.sleepEntries || [];

      const newEntry = {
        date,
        hours: hoursNumber,
        quality,
        wakeState,
        notes: notes.trim(),
        updatedAt: new Date().toISOString(),
      };

      const existingIndex = currentEntries.findIndex((e) => e.date === date);
      let updatedEntries;
      if (existingIndex >= 0) {
        updatedEntries = [...currentEntries];
        updatedEntries[existingIndex] = {
          ...currentEntries[existingIndex],
          ...newEntry,
        };
      } else {
        updatedEntries = [...currentEntries, newEntry];
      }

      updatedEntries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

      await updateDoc(userRef, { sleepEntries: updatedEntries });
      setEntries(
        updatedEntries.map((e) => ({
          ...e,
          quality:
            typeof e.quality === 'number'
              ? e.quality
              : QUALITY_LABELS[e.quality] || 3,
          wakeState: typeof e.wakeState === 'number' ? e.wakeState : 3,
        }))
      );

      setEditingDate(null);
      setSnackbar({
        open: true,
        message: 'Registro de sono salvo com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao salvar registro de sono:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar. Tente novamente.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setDate(entry.date);
    setHours(entry.hours);
    setQuality(entry.quality || 3);
    setWakeState(entry.wakeState || 3);
    setNotes(entry.notes || '');
    setEditingDate(entry.date);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (entry) => {
    if (!currentUser?.uid) return;
    if (!window.confirm(`Apagar o registro de sono de ${entry.date ? new Date(entry.date + 'T00:00:00').toLocaleDateString('pt-BR') : entry.date}?`)) return;
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const currentEntries = data.sleepEntries || [];
      const updatedEntries = currentEntries.filter((e) => e.date !== entry.date);
      await updateDoc(userRef, { sleepEntries: updatedEntries });
      setEntries(
        updatedEntries.map((e) => ({
          ...e,
          quality: typeof e.quality === 'number' ? e.quality : 3,
          wakeState: typeof e.wakeState === 'number' ? e.wakeState : 3,
        }))
      );
      if (editingDate === entry.date) {
        setEditingDate(null);
        setDate(new Date().toISOString().split('T')[0]);
      }
      setSnackbar({
        open: true,
        message: 'Registro de sono apagado.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao apagar registro de sono:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao apagar. Tente novamente.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <PageLayout title="Sono" customColors={customColors} hideAddButton>
      <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 8 }}>
        {/* 1️⃣ Registro rápido */}
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
            >
              Registro rápido de sono
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Data"
                type="date"
                fullWidth
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  🕒 Quantas horas você dormiu?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1, px: 1 }}>
                    <Slider
                      min={0}
                      max={12}
                      step={0.5}
                      value={hours}
                      onChange={(_, value) => setHours(value)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}h`}
                    />
                  </Box>
                  <TextField
                    label="Horas"
                    type="number"
                    size="small"
                    value={hours}
                    onChange={(e) => setHours(e.target.value === '' ? '' : Number(e.target.value))}
                    inputProps={{ min: 0, max: 24, step: 0.5 }}
                    sx={{ width: 90 }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  ⭐ Qualidade do sono
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating
                    name="sleep-quality"
                    value={quality}
                    onChange={(_, newValue) => setQuality(newValue || 1)}
                    max={5}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {QUALITY_LABELS[quality]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: { xs: 0.5, sm: 5 } }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  ⚡ Como acordou?
                </Typography>
                <Slider
                  min={1}
                  max={5}
                  step={1}
                  marks={[
                    { value: 1, label: 'Muito cansado' },
                    { value: 3, label: 'Neutro' },
                    { value: 5, label: 'Muito disposto' },
                  ]}
                  value={wakeState}
                  onChange={(_, value) => setWakeState(value)}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Estado atual: <strong>{WAKE_LABELS[wakeState]}</strong>
                </Typography>
              </Box>

              <TextField
                label="Observações (opcional)"
                multiline
                minRows={3}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading
                    ? 'Salvando...'
                    : editingDate
                    ? 'Atualizar registro'
                    : 'Salvar registro'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 2️⃣ Histórico abaixo */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'primary.main', mb: 1.5 }}
            >
              Histórico de sono
            </Typography>
            {entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum registro ainda. Comece registrando sua próxima noite de sono.
              </Typography>
            ) : (
              <List dense>
                {entries.map((entry) => {
                  const dateLabel = entry.date
                    ? new Date(entry.date + 'T00:00:00').toLocaleDateString(
                        'pt-BR'
                      )
                    : '';
                  const qualityStars = '★★★★★'.slice(0, entry.quality || 0);
                  return (
                    <ListItem
                      key={entry.date}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            edge="end"
                            aria-label="editar"
                            onClick={() => handleEdit(entry)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="apagar registro"
                            onClick={() => handleDelete(entry)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={`${dateLabel} — ${entry.hours}h — ${qualityStars}`}
                        secondary={`Qualidade: ${
                          QUALITY_LABELS[entry.quality] || ''
                        } · Como acordou: ${
                          WAKE_LABELS[entry.wakeState] || ''
                        }`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Dicas de higiene do sono */}
        <SleepTips />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

